// src/views/parent/PAAide.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

// ─── CARTE STANDARD ─────────────────────────────────────────────────────
function InfoCard({ icon, iconColor, title, children }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="info-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-2px)" : "none",
        marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: `${iconColor}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className={`ti ${icon}`} style={{ color: iconColor, fontSize: 20 }} />
        </div>
        <h3
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: 16,
            fontWeight: "700",
            color: "var(--t)",
            margin: 0,
          }}
        >
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

// ─── ÉLÉMENT DE FONCTIONNALITÉ ─────────────────────────────────────────
function FeatureItem({ icon, text }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
      <i className={`ti ${icon}`} style={{ color: "var(--pr)", fontSize: 16, marginTop: 2 }} />
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          color: "var(--m)",
          lineHeight: 1.4,
          flex: 1,
        }}
      >
        {text}
      </span>
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────────
function AideHeader({ etablissement, loading, slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();
  const nomEcole = etablissement?.nom || "Suivi Scolaire";
  const slogan = etablissement?.slogan || "L'innovation numérique au cœur de l'éducation";

  return (
    <div className="aide-header">
      {/* Bouton Menu (Drawer) */}
      <button className="menu-btn" onClick={openDrawer}>
        <i className="ti ti-menu-2" />
      </button>

      <div className="header-content">
        <div className="icon-wrapper">
          <i className="ti ti-info-circle" />
        </div>
        <h1>À propos</h1>
        <p>{loading ? "Chargement..." : `${nomEcole} - ${slogan}`}</p>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────
export default function PAAide({ slug }) {
  const navigate = useNavigate();
  const { getEtablissement } = useParentService();
  const [etablissement, setEtablissement] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── CHARGEMENT DES INFOS ──────────────────────────────────────────
  useEffect(() => {
    const fetchEtablissement = async () => {
      if (!slug) return;

      try {
        const data = await getEtablissement(slug);
        setEtablissement(data);
      } catch (err) {
        // Utiliser des valeurs par défaut en cas d'erreur
        setEtablissement({
          nom: "Suivi Scolaire",
          slogan: "L'innovation numérique au cœur de l'éducation",
          mission: "Faciliter la communication entre l'école et les parents en offrant un suivi précis et en temps réel de la présence des élèves, tout en garantissant la sécurité et la confidentialité des données."
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEtablissement();
  }, [slug]);

  const handleLogout = () => {
    localStorage.removeItem("authTokens");
    window.location.href = "/login";
  };

  const nomEcole = etablissement?.nom || "Suivi Scolaire";
  const mission = etablissement?.mission ||
    "Faciliter la communication entre l'école et les parents en offrant un suivi précis et en temps réel de la présence des élèves, tout en garantissant la sécurité et la confidentialité des données.";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .aide-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .aide-header {
          height: 240px;
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          border-radius: 0 0 40px 40px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          position: relative;
          overflow: hidden;
        }

        .aide-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .aide-header .menu-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: background 0.15s;
        }

        .aide-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .aide-header .header-content {
          position: absolute;
          bottom: 24px;
          left: 0;
          right: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 0 20px;
        }

        .aide-header .header-content .icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .aide-header .header-content .icon-wrapper i {
          font-size: 28px;
          color: #fff;
        }

        .aide-header .header-content h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          margin: 0;
        }

        .aide-header .header-content p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.8);
          font-size: 13px;
          margin: 0;
        }

        .aide-content {
          padding: 16px;
        }

        .info-card {
          animation: fadeUp 0.4s ease both;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 480px) {
          .aide-header {
            height: 200px;
          }
          .aide-header .header-content h1 {
            font-size: 20px;
          }
          .aide-content {
            padding: 12px;
          }
        }
      `}</style>

      <div className="aide-page">
        <AideHeader
          etablissement={etablissement}
          loading={loading}
          slug={slug}
        />

        <div className="aide-content">
          {/* Carte Présentation */}
          <div style={{ animationDelay: "0s" }}>
            <InfoCard
              icon="ti-info-circle"
              iconColor="#1B4F80"
              title="Présentation"
            >
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13.5,
                  color: "var(--m)",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Solution numérique innovante dédiée à la gestion
                et au suivi des présences scolaires.
              </p>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13.5,
                  color: "var(--m)",
                  lineHeight: 1.6,
                  marginTop: 10,
                }}
              >
                Conçue pour faciliter la communication entre l'école et les
                parents, notre plateforme permet un suivi précis et en temps
                réel de l'assiduité des élèves.
              </p>
            </InfoCard>
          </div>

          {/* Carte Notre Mission */}
          <div style={{ animationDelay: "0.05s" }}>
            <InfoCard
              icon="ti-bolt"
              iconColor="#3b6d11"
              title="Notre Mission"
            >
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13.5,
                  color: "var(--m)",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {mission}
              </p>
            </InfoCard>
          </div>

          {/* Carte Fonctionnalités principales */}
          <div style={{ animationDelay: "0.1s" }}>
            <InfoCard
              icon="ti-rocket"
              iconColor="#1B4F80"
              title="Fonctionnalités principales"
            >
              <FeatureItem
                icon="ti-bell"
                text="Notification immédiate des parents en cas d'absence ou de retard"
              />
              <FeatureItem
                icon="ti-messages"
                text="Messagerie sécurisée entre parents et école"
              />
              <FeatureItem
                icon="ti-chart-bar"
                text="Suivi statistique des présences par période"
              />
              <FeatureItem
                icon="ti-calendar"
                text="Calendrier interactif avec historique des pointages"
              />
              <FeatureItem
                icon="ti-file-text"
                text="Consultation des bulletins scolaires"
              />
            </InfoCard>
          </div>
        </div>
      </div>
    </>
  );
}